import React, { useRef, useEffect, useState } from "react";
import { useAppContext } from "../../contexts/appContext";
import { CanvasMode, NavEvents } from "../../contexts/types";
import {
  cameraOrbit,
  cameraTarget,
  CanvasDataEvent,
  EventPayload,
  EventTypes,
} from "../../providers/types";
import { exploreToggleEventCheck } from "../../utils/eventUtils";
import { useFocusCheck } from "../../hooks/useFocusCheck";
import { VALID_KEYS_WITH_ZOOM } from "../../constants";
import Spinner from "../spinner";
import { PopUp } from "../popUps/popUp";
import { Modal } from "../modal/modal";

// Type declarations
declare global {
  namespace JSX {
    interface IntrinsicElements {
      "model-viewer": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        src?: string;
        "camera-controls": undefined | boolean;
        "auto-rotate"?: boolean;
        autoplay?: boolean;
        alt?: string;
        "camera-orbit"?: string;
        "interaction-prompt"?: "auto" | "when-focused" | "none";
      };
    }
  }
}

interface ModelViewerElement extends HTMLElement {
  getCameraOrbit(): cameraOrbit;
  getFieldOfView(): number;
  getCameraTarget(): cameraTarget;
  cameraOrbit: string;
  cameraTarget: string;
  fieldOfView: string;
  jumpCameraToGoal(): void;
}

interface CameraPosition {
  cameraOrbit: cameraOrbit;
  fieldOfView: number;
  cameraTarget: cameraTarget;
}

interface SimpleModelViewerProps {
  src: string;
  alt?: string;
  onCameraChange: (position: CameraPosition) => void;
  style?: React.CSSProperties;
  interactionPrompt?: "auto" | "when-focused" | "none";
  receivedCameraPosition?: CameraPosition;
  leader?: boolean;
  isExploreMode?: boolean;
  events?: CanvasDataEvent[];
  mode: CanvasMode;
}

const SimpleModelViewer: React.FC<SimpleModelViewerProps> = ({
  src,
  alt = "3D Model",
  onCameraChange,
  style = { width: "100%", height: "100%" },
  interactionPrompt = "none",
  receivedCameraPosition,
  leader = false,
  isExploreMode = false,
  events,
  mode,
}) => {
  const modelViewerRef = useRef<ModelViewerElement>(null);
  const { generateEventHandlers, state } = useAppContext();
  const { playerHasFocus } = useFocusCheck();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  // Load Model Viewer script
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "module";
    script.src =
      "https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js";

    if (!document.querySelector('script[src*="model-viewer"]')) {
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;
    if (events?.length) {
      const canvasDataEv = events[events.length - 1];
      const eventPayload: EventPayload = JSON.parse(canvasDataEv.event);

      // if we switch back to explore mode we need to send the instructor view
      // to the students
      if (
        leader &&
        exploreToggleEventCheck(eventPayload).isExploreEvent &&
        !exploreToggleEventCheck(eventPayload).exploreModeValue &&
        canvasDataEv.isSelf
      ) {
        const modelViewer = modelViewerRef.current;
        const orbit = modelViewer.getCameraOrbit();
        const fov = modelViewer.getFieldOfView();
        const target = modelViewer.getCameraTarget();

        onCameraChange({
          cameraOrbit: orbit,
          fieldOfView: fov,
          cameraTarget: target,
        });
        return;
      }
      // on replay we don't have an instructor to ask for their view so
      // we have to cycle back through the events to find one that has
      // 3d view attached to it and then we set our replay view to that
      if (
        mode === CanvasMode.REPLAY &&
        exploreToggleEventCheck(eventPayload).isExploreEvent &&
        !exploreToggleEventCheck(eventPayload).exploreModeValue
      ) {
        for (let i = events.length - 1; i >= 0; i--) {
          const eventPayload: EventPayload = JSON.parse(canvasDataEv.event);
          if (eventPayload.type === EventTypes.THREE_DEE_DRAG) {
            modelViewer.cameraOrbit = `${eventPayload.threeDeeData?.cameraOrbit?.theta}rad ${eventPayload.threeDeeData?.cameraOrbit?.phi}rad ${eventPayload.threeDeeData?.cameraOrbit?.radius}m`;
            modelViewer.cameraTarget = `${eventPayload.threeDeeData?.cameraTarget?.x}m ${eventPayload.threeDeeData?.cameraTarget?.y}m ${eventPayload.threeDeeData?.cameraTarget?.z}m`;
            modelViewer.fieldOfView = `${eventPayload.threeDeeData?.fieldOfView}deg`;
            break;
          }
        }
        return;
      }
    }
  }, [events.length, leader, mode]);

  const isApplyingExternalUpdateRef = useRef(false);
  const isUserInteractingRef = useRef(false);
  const externalUpdateTimeoutRef = useRef(null);
  const hasUserInteractedRef = useRef(false);

  // Track user interaction start/end
  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    const handleInteractionStart = () => {
      isUserInteractingRef.current = true;
      hasUserInteractedRef.current = true;
    };

    const handleInteractionEnd = () => {
      isUserInteractingRef.current = false;
    };

    // Listen for mouse/touch interactions
    modelViewer.addEventListener("mousedown", handleInteractionStart);
    modelViewer.addEventListener("mouseup", handleInteractionEnd);
    modelViewer.addEventListener("touchstart", handleInteractionStart);
    modelViewer.addEventListener("touchend", handleInteractionEnd);

    // Also listen for keyboard
    const handleKeyDown = (e) => {
      if (
        [
          "w",
          "a",
          "s",
          "d",
          "q",
          "e",
          "arrowup",
          "arrowdown",
          "arrowleft",
          "arrowright",
        ].includes(e.key.toLowerCase())
      ) {
        isUserInteractingRef.current = true;
        hasUserInteractedRef.current = true;
      }
    };

    const handleKeyUp = (e) => {
      if (
        [
          "w",
          "a",
          "s",
          "d",
          "q",
          "e",
          "arrowup",
          "arrowdown",
          "arrowleft",
          "arrowright",
        ].includes(e.key.toLowerCase())
      ) {
        // Delay clearing to handle held keys
        setTimeout(() => {
          isUserInteractingRef.current = false;
        }, 100);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      modelViewer.removeEventListener("mousedown", handleInteractionStart);
      modelViewer.removeEventListener("mouseup", handleInteractionEnd);
      modelViewer.removeEventListener("touchstart", handleInteractionStart);
      modelViewer.removeEventListener("touchend", handleInteractionEnd);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [src]);

  // First useEffect - Apply received camera position
  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer || !receivedCameraPosition) return;

    // Don't apply external updates if user is actively interacting
    if (isUserInteractingRef.current) {
      return;
    }

    // Set flag to prevent broadcasting
    isApplyingExternalUpdateRef.current = true;

    // Clear any existing timeout
    if (externalUpdateTimeoutRef.current) {
      clearTimeout(externalUpdateTimeoutRef.current);
    }

    // Apply camera changes
    modelViewer.cameraOrbit = `${receivedCameraPosition?.cameraOrbit?.theta}rad ${receivedCameraPosition?.cameraOrbit?.phi}rad ${receivedCameraPosition?.cameraOrbit?.radius}m`;
    modelViewer.cameraTarget = `${receivedCameraPosition?.cameraTarget?.x}m ${receivedCameraPosition?.cameraTarget?.y}m ${receivedCameraPosition?.cameraTarget?.z}m`;
    modelViewer.fieldOfView = `${receivedCameraPosition?.fieldOfView}deg`;

    // Keep flag set for duration of interpolation animation
    externalUpdateTimeoutRef.current = setTimeout(() => {
      isApplyingExternalUpdateRef.current = false;
    }, 2000);
  }, [receivedCameraPosition]);

  // Second useEffect - Listen for camera changes and broadcast
  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    let isMoving = false;
    let movementTimeout;
    let updateInterval;

    const sendCameraUpdate = () => {
      const orbit = modelViewer.getCameraOrbit();
      const fov = modelViewer.getFieldOfView();
      const target = modelViewer.getCameraTarget();

      onCameraChange({
        cameraOrbit: orbit,
        fieldOfView: fov,
        cameraTarget: target,
      });
    };

    const startMovement = () => {
      if (!isMoving) {
        isMoving = true;
        // Send update immediately when movement starts
        sendCameraUpdate();
        updateInterval = setInterval(() => {
          // Send update immediately while moving every second
          sendCameraUpdate();
        }, 1000);
      }
    };

    const stopMovement = () => {
      if (isMoving) {
        isMoving = false;
        if (updateInterval) {
          clearInterval(updateInterval);
          updateInterval = null;
        }
        // send final position
        sendCameraUpdate();
      }
    };

    const handleCameraChange = () => {
      // Don't broadcast if:
      // 1. Not a leader, OR
      // 2. Currently applying an external update
      if (
        !leader ||
        isApplyingExternalUpdateRef.current ||
        !hasUserInteractedRef.current
      )
        return;

      startMovement();

      if (movementTimeout) {
        clearTimeout(movementTimeout);
      }

      movementTimeout = setTimeout(() => {
        stopMovement();
      }, 500);
    };

    modelViewer.addEventListener("camera-change", handleCameraChange);

    return () => {
      if (movementTimeout) clearTimeout(movementTimeout);
      if (updateInterval) clearInterval(updateInterval);
      modelViewer.removeEventListener("camera-change", handleCameraChange);
    };
  }, [onCameraChange, leader]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (externalUpdateTimeoutRef.current) {
        clearTimeout(externalUpdateTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    const handleKeyDown = (event) => {
      // Only allow keyboard controls for teachers or when in explore mode
      if (!leader && !isExploreMode) return;
      // if (document.activeElement !== modelViewer) return;
      if (VALID_KEYS_WITH_ZOOM.indexOf(event.key.toLowerCase()) < 0) return;
      if (!playerHasFocus()) {
        const currentOrbit = modelViewer.getCameraOrbit();
        const currentFOV = modelViewer.getFieldOfView();
        let newTheta = currentOrbit.theta;
        let newPhi = currentOrbit.phi;
        let newRadius = currentOrbit.radius;
        let newFOV = currentFOV;

        const PERCENTAGE_STEP = 0.05; // 5%
        hasUserInteractedRef.current = true;

        switch (event.key.toLowerCase()) {
          case "w":
          case "arrowup":
            // Move up by 5% of total vertical range (π radians = 180°)
            const verticalStep = Math.PI * PERCENTAGE_STEP; // 5% of π ≈ 0.157 rad
            newPhi = Math.max(-Math.PI / 2, newPhi - verticalStep);
            break;

          case "s":
          case "arrowdown":
            // Move down (increase phi towards π)
            const verticalStepDown = Math.PI * PERCENTAGE_STEP;
            newPhi = Math.min(Math.PI, newPhi + verticalStepDown);
            break;

          case "a":
          case "arrowleft":
            // Rotate left by 5% of full rotation (2π radians = 360°)
            const thetaStep = 2 * Math.PI * PERCENTAGE_STEP; // 5% of 2π ≈ 0.314 rad
            newTheta = newTheta - thetaStep;
            break;

          case "d":
          case "arrowright":
            // Rotate right by 5% of full rotation
            const thetaStepRight = 2 * Math.PI * PERCENTAGE_STEP;
            newTheta = newTheta + thetaStepRight;
            break;

          case "q":
          case "-":
            // Zoom out by 5% of current distance
            const zoomOutStep = currentOrbit.radius * PERCENTAGE_STEP;
            newRadius = newRadius + zoomOutStep;
            newFOV = Math.min(120, currentFOV * 1.3);
            break;

          case "e":
          case "+":
          case "=":
            // Zoom in by 5% of current distance
            const zoomInStep = currentOrbit.radius * PERCENTAGE_STEP;
            newRadius = Math.max(0.1, newRadius - zoomInStep);
            newFOV = Math.max(10, currentFOV * 0.7);
            break;

          default:
            return;
        }

        // Prevent default browser behavior
        event.preventDefault();

        modelViewer.cameraOrbit = `${newTheta}rad ${newPhi}rad ${newRadius}m`;
        modelViewer.fieldOfView = `${newFOV}deg`;
      }
    };

    // Add event listener to document or specific element
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [leader, isExploreMode, onCameraChange, playerHasFocus]);

  const adjustCamera = (type) => {
    const modelViewer = modelViewerRef.current;
    hasUserInteractedRef.current = true;

    switch (type) {
      case "home":
        modelViewer.cameraOrbit = "auto auto auto";
        modelViewer.cameraTarget = "auto auto auto";
        modelViewer.fieldOfView = "auto";

        break;

      case "zoomIn":
        const currentFOVIn = modelViewer.getFieldOfView();
        const newFOVIn = Math.max(10, currentFOVIn * 0.7);
        modelViewer.fieldOfView = `${newFOVIn}deg`;

        break;

      case "zoomOut":
        const currentFOVOut = modelViewer.getFieldOfView();
        const newFOVOut = Math.min(120, currentFOVOut * 1.3);
        modelViewer.fieldOfView = `${newFOVOut}deg`;

        break;
    }
  };

  useEffect(() => {
    // Set event handlers for canvas button interactions via slide manager
    generateEventHandlers({
      [NavEvents.FIT]: () => adjustCamera("home"),
      [NavEvents.ZOOM_IN]: () => adjustCamera("zoomIn"),
      [NavEvents.ZOOM_OUT]: () => adjustCamera("zoomOut"),
    });
  }, [generateEventHandlers]);

  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    const handleLoad = () => {
      setLoaded(true);
      setTimeout(() => {
        modelViewerRef.current.focus();
      }, 10);
    };

    const handleError = (event) => {
      setError(true);
    };

    // When model is successfully loaded
    modelViewer.addEventListener("load", handleLoad);

    // When model fails to load
    modelViewer.addEventListener("error", handleError);

    return () => {
      modelViewer.removeEventListener("load", handleLoad);
      modelViewer.removeEventListener("error", handleError);
    };
  }, []);

  return (
    <>
      {!loaded && (
        <div style={{ margin: "40vh auto" }}>
          <Spinner />
        </div>
      )}
      <Modal
        dismissible={true}
        display={error}
        onDismiss={() => setError(false)}
      >
        <div className="__modal-content">
          <PopUp
            userMessage={"Something went wrong loading this object"}
            popUpHeader="Uh oh!"
          />
        </div>
      </Modal>

      <model-viewer
        ref={modelViewerRef}
        src={src}
        key={src}
        alt={alt}
        camera-controls={leader || isExploreMode ? true : undefined}
        interaction-prompt={interactionPrompt}
        style={style}
        disable-pan
        camera-orbit="auto auto auto"
        field-of-view="auto"
        camera-target="auto auto auto"
        autoplay
        interpolation-decay={!leader && !isExploreMode ? "200" : "100"}
      />
    </>
  );
};

export default SimpleModelViewer;
